import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useWebSocket } from '@/hooks/useWebSocket'
import { conversationsApi } from '@/lib/api/conversations'
import { useIncomingNotificationStore } from '@/store/useIncomingNotificationStore'
import type { IncomingChatData } from '../components/IncomingChatNotification'

interface NotificationEvent {
  type: 'incoming_chat_notification' | 'notification_accepted' | 'notification_expired'
  sessionId?: string
  notificationId?: string
  customerName?: string
  customerEmail?: string
  widgetName?: string
  reason?: string
  createdAt?: number
  expiresAt?: number
  acceptedBy?: string
  agentName?: string
}

export const useIncomingChatNotifications = () => {
  const { addNotification, removeNotification, removeByNotificationId } = useIncomingNotificationStore()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const handleNotificationEvent = useCallback((event: NotificationEvent) => {
    console.log('[Notification WS] Event received:', event.type, event)

    switch (event.type) {
      case 'incoming_chat_notification':
        if (event.sessionId && event.notificationId && event.expiresAt) {
          const notification: IncomingChatData = {
            sessionId: event.sessionId,
            notificationId: event.notificationId,
            customerName: event.customerName,
            customerEmail: event.customerEmail,
            widgetName: event.widgetName,
            reason: event.reason,
            createdAt: event.createdAt || Date.now(),
            expiresAt: event.expiresAt,
          }

          addNotification(notification)
          console.log('[Notification WS] Added incoming chat notification:', event.sessionId)
        }
        break

      case 'notification_accepted':
        if (event.sessionId) {
          removeNotification(event.sessionId)

          if (event.agentName) {
            enqueueSnackbar(`Chat accepted by ${event.agentName}`, {
              variant: 'info',
              autoHideDuration: 3000,
            })
          }

          console.log('[Notification WS] Notification accepted, removed:', event.sessionId)
        }
        break

      case 'notification_expired':
        if (event.sessionId) {
          removeNotification(event.sessionId)

          enqueueSnackbar('Chat notification expired - no agent accepted', {
            variant: 'warning',
            autoHideDuration: 5000,
          })

          console.log('[Notification WS] Notification expired, removed:', event.sessionId)
        }
        break
    }
  }, [addNotification, removeNotification, enqueueSnackbar])

  // Connect to notification WebSocket
  useWebSocket<NotificationEvent>('/chat/notifications/ws', {
    enabled: true,
    onMessage: handleNotificationEvent,
    onOpen: () => {
      console.log('Connected to notification system')
    },
    onError: (error) => {
      console.error('Notification WebSocket error:', error)
    },
    onClose: () => {
      console.log('Disconnected from notification system')
    },
  })

  const acceptChat = useCallback(
    async (sessionId: string) => {
      try {
        // Call API to accept the notification
        await conversationsApi.acceptNotification(sessionId)

        // Remove notification immediately for this agent
        removeNotification(sessionId)

        // Navigate to the conversation
        navigate(`/conversations/${sessionId}`)

        enqueueSnackbar('Chat accepted successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        })
      } catch (error) {
        console.error('Failed to accept chat:', error)
        // Extract actual server error message from Axios error response
        let errorMessage = 'Failed to accept chat'
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { data?: { error?: string } } }
          errorMessage = axiosError.response?.data?.error || errorMessage
        } else if (error instanceof Error) {
          errorMessage = error.message
        }
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          autoHideDuration: 5000,
        })
      }
    },
    [navigate, enqueueSnackbar, removeNotification]
  )

  const dismissNotification = useCallback((notificationId: string) => {
    removeByNotificationId(notificationId)
  }, [removeByNotificationId])

  // Auto-cleanup expired notifications
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const notifications = useIncomingNotificationStore.getState().notifications
      for (const [sessionId, notification] of notifications.entries()) {
        if (notification.expiresAt <= now) {
          removeNotification(sessionId)
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [removeNotification])

  // Read notifications from the store
  const activeNotifications = Array.from(
    useIncomingNotificationStore((s) => s.notifications).values()
  )

  return {
    activeNotifications,
    acceptChat,
    dismissNotification,
  }
}
